<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddWhatsappAccount from './AddWhatsappAccount.vue';
import ScanQRCode from './ScanQRCode.vue';
import EditAgent from './EditWhatsappAccount.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { OnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const showScanPopup = ref(false);
const showLogoutPopup = ref(false);
const whatsappAccountAPI = ref({ message: '' });
const currentWhatsappAccount = ref({});

const emit = defineEmits(['tabChange']);

const deleteConfirmText = computed(
  () =>
    `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentWhatsappAccount.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentWhatsappAccount.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentWhatsappAccount.value.name}?`;
});

const whatsappAccountList = computed(
  () => getters['whatsappAccounts/getWhatsappAccounts'].value
);
const uiFlags = computed(() => getters['whatsappAccounts/getUIFlags'].value);

onMounted(() => {
  store.dispatch('whatsappAccounts/fetch');
});

const showEditAction = whatsapp_account => {
  return true;
};

const showScanAction = whatsapp_account => {
  return whatsapp_account.online !== 'online';
};

const showDeleteAction = whatsapp_account => {
  return whatsapp_account.online !== 'online';
};

const showAlertMessage = message => {
  loading.value[currentWhatsappAccount.value.id] = false;
  currentWhatsappAccount.value = {};
  whatsappAccountAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
  store.dispatch('whatsappAccounts/fetch');
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentWhatsappAccount.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openScanPopup = whatsapp => {
  showScanPopup.value = true;
  currentWhatsappAccount.value = whatsapp;
};

const openLogoutPopup = whatsapp => {
  showLogoutPopup.value = true;
  currentWhatsappAccount.value = whatsapp;
};

const rcdoeRef = ref(null);
const hideScanPopup = () => {
  showScanPopup.value = false;
  rcdoeRef.value.clear();
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentWhatsappAccount.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteWhatsAccount = async id => {
  try {
    await store.dispatch('whatsappAccounts/delete', id);
    showAlertMessage('SUCCESS_MESSAGE');
  } catch (error) {
    showAlertMessage('ERROR_MESSAGE');
  }
};
const confirmDeletion = () => {
  loading.value[currentWhatsappAccount.value.id] = true;
  closeDeletePopup();
  deleteWhatsAccount(currentWhatsappAccount.value.id);
};

const closeConfirmationPopup = () => {
  showLogoutPopup.value = false;
};

const confirmLogout = () => {
  loading[currentWhatsappAccount.value.id] = true;
  closeConfirmationPopup();
  store
    .dispatch(
      'whatsappAccounts/logoutWhatsappAccount',
      currentWhatsappAccount.value.id
    )
    .then(() => {
      showAlertMessage('Success');
    })
    .catch(error => {
      showAlertMessage('Fail');
    });
};

const inboxes = useMapGetter('inboxes/getWhatsappPersonalInboxes');
const tabs = computed(() => {
  const allTabCount = whatsappAccountList.value.filter(account => {
    return (
      statusNameButtom.value === 'All' ||
      (statusNameButtom.value === 'Online' && account.online === 'online') ||
      (statusNameButtom.value === 'Offline' && account.online !== 'online')
    );
  }).length;

  const allTab = {
    label: 'ALL',
    value: 'all',
    count: allTabCount,
  };

  const inboxTabs = inboxes.value.map(tab => {
    const count = whatsappAccountList.value.filter(account => {
      return (
        account.inbox_name === tab.name &&
        (statusNameButtom.value === 'All' ||
          (statusNameButtom.value === 'Online' && account.online === 'online') ||
          (statusNameButtom.value === 'Offline' && account.online !== 'online'))
      );
    }).length;

    return {
      label: tab.name,
      value: tab.id,
      count: count,
    };
  });

  inboxTabs.unshift(allTab); // Add the "ALL" option at the beginning

  return inboxTabs;
});

const selectedInbox = ref('ALL'); // Default to 'all'
const statusNameButtom = ref('All');

const filteredWhatsappAccountList = computed(() => {
  let accountList = whatsappAccountList.value;

  // Filter by selected inbox
  if (selectedInbox.value !== 'ALL') {
    accountList = accountList.filter(account => account.inbox_name === selectedInbox.value);
  }

  // Filter by online status
  if (statusNameButtom.value === 'Online') {
    accountList = accountList.filter(account => account.online === 'online');
  } else if (statusNameButtom.value === 'Offline') {
    accountList = accountList.filter(account => account.online === 'offline');
  }

  return accountList;
});

const activeTabIndex = computed(() => {
  return tabs.value.findIndex(v => v.label === selectedInbox.value);
});

const statusName = computed(() => {
  return tabs.value.findIndex(v => v.label === selectedInbox.value);
});
const handleTabChange = value => {
  emit('tabChange', value);
  selectedInbox.value = value.label; // Update the selected inbox based on the tab change
};

const STATUS_IMTES = [
  {
    label: 'All',
    value: 'all',
    action: 'filter',
  },
  {
    label: 'Online',
    value: 'online',
    action: 'filter',
  },
  {
    label: 'Offline',
    value: 'offline',
    action: 'filter',
  },
];

const isStatusMenuOpen = ref(false);
const handleLocaleAction = ({ value }) => {
  isStatusMenuOpen.value = false;
  statusNameButtom.value = STATUS_IMTES.find(
    item => item.value === value
  )?.label;
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!whatsappAccountList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="'Whatsapp Account'"
        :description="'A WhatsApp Account integrates with your system for seamless message management. Simply log in and scan a QR code to authenticate. Once set up, you can send and receive messages directly from your inbox.'"
      >
        <template #actions>
          <woot-button
            class="rounded-md button nice"
            icon="add-circle"
            @click="openAddPopup"
          >
            Add Whatsapp Account
          </woot-button>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex flex-col items-start w-full gap-2 lg:flex-row">
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTabIndex"
        @tab-changed="handleTabChange"
      />
      <div class="relative group">
        <OnClickOutside @trigger="isStatusMenuOpen = false">
          <Button
            :label="statusNameButtom"
            size="sm"
            icon="i-lucide-chevron-down"
            color="slate"
            trailing-icon
            @click="isStatusMenuOpen = !isStatusMenuOpen"
          />

          <DropdownMenu
            v-if="isStatusMenuOpen"
            :menu-items="STATUS_IMTES"
            class="left-0 w-40 max-w-[300px] mt-2 overflow-y-auto xl:right-0 top-full max-h-60"
            @action="handleLocaleAction"
          />
        </OnClickOutside>
      </div>
      </div>
      <table class="divide-y divide-slate-75 dark:divide-slate-700">
        <tbody
          class="divide-y divide-slate-50 dark:divide-slate-800 text-slate-700 dark:text-slate-300"
        >
          <tr
            v-for="(whatsapp_account, index) in filteredWhatsappAccountList"
            :key="whatsapp_account.phone_number"
          >
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-row items-center gap-4">
                <Thumbnail
                  :src="whatsapp_account.avatar"
                  :username="whatsapp_account.name"
                  size="40px"
                  :status="whatsapp_account.online"
                />
                <div>
                  <span class="block font-medium capitalize">
                    {{ whatsapp_account.name }}
                  </span>
                  <span>{{ whatsapp_account.phone_number }}</span>
                </div>
              </div>
            </td>
            <td class="py-4">
              <span class="block font-medium capitalize">
                {{ whatsapp_account.inbox_name }}
              </span>
            </td>
            <td class="py-4">
              <div class="flex justify-end gap-1">
                <woot-button
                  v-if="showScanAction(whatsapp_account)"
                  v-tooltip.top="'Scan'"
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  icon="qrcode"
                  class-names="grey-btn"
                  @click="openScanPopup(whatsapp_account)"
                />

                <woot-button
                  v-if="!showScanAction(whatsapp_account)"
                  v-tooltip.top="'Logout'"
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  icon="power"
                  :is-loading="loading[whatsapp_account.id]"
                  @click="openLogoutPopup(whatsapp_account, index)"
                />

                <!--                <woot-button-->
                <!--                  v-if="showEditAction(whatsapp_account)"-->
                <!--                  v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"-->
                <!--                  variant="smooth"-->
                <!--                  size="tiny"-->
                <!--                  color-scheme="secondary"-->
                <!--                  icon="edit"-->
                <!--                  class-names="grey-btn"-->
                <!--                  @click="openEditPopup(whatsapp_account)"-->
                <!--                />-->
                <!--                <woot-button-->
                <!--                  v-if="showDeleteAction(whatsapp_account)"-->
                <!--                  v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"-->
                <!--                  variant="smooth"-->
                <!--                  color-scheme="alert"-->
                <!--                  size="tiny"-->
                <!--                  icon="dismiss-circle"-->
                <!--                  class-names="grey-btn"-->
                <!--                  :is-loading="loading[whatsapp_account.id]"-->
                <!--                  @click="openDeletePopup(whatsapp_account, index)"-->
                <!--                />-->
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddWhatsappAccount @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showScanPopup" @close="hideScanPopup">
      <ScanQRCode
        ref="rcdoeRef"
        :id="currentWhatsappAccount.id"
        :edname="currentWhatsappAccount.name"
        :edonline="currentWhatsappAccount.online"
        :on-close="hideScanPopup"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showLogoutPopup"
      :on-close="closeConfirmationPopup"
      :on-confirm="confirmLogout"
      :title="'Logout'"
      :message="'Logout account'"
      :message-value="''"
      :confirm-text="'Yes'"
      :reject-text="'No'"
    />
  </SettingsLayout>
</template>
